import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { ChatAIService } from '@/services/ChatAIService';
import { chatAIHub } from '@/signalr/hubs/ChatAIHub';
import { IChatAIHealth } from '@/models/chatai/IChatAIHealth';
import { sendNotification } from '@srm/ui/lib/notifications';
import { TOAST_OFFLINE, TOAST_ONLINE } from '@/components/molecules/ChatAI/constants';

const POLL_INTERVAL_MS = 30_000;
const VISIBILITY_REFRESH_COOLDOWN_MS = 10_000;

// Module-level — стабильные ссылки, чтобы useSyncExternalStore не пересубскрайбился на каждом ренде.
const subscribeToHubState = (onStoreChange: () => void) =>
    chatAIHub.onStateChange(() => onStoreChange());
const getHubStateSnapshot = () => chatAIHub.connectionState;

export type ChatAIIndicatorStatus = 'online' | 'degraded' | 'reconnecting' | 'offline' | 'unknown';

export interface UseChatAIStatusOptions {
    /** Активен ли индикатор. true — пока чат открыт (expanded или minimized). */
    enabled: boolean;
}

export interface UseChatAIStatusResult {
    status: ChatAIIndicatorStatus;
    health: IChatAIHealth | null;
    lastUpdate: number | null;
    /** true когда бэк сообщил `not_configured` — UI может показать особый hint. */
    notConfigured: boolean;
}

/**
 * Поднимает SignalR-соединение и периодический health-poll, пока `enabled === true`.
 * Сводный статус — derived из (health.status, connectionState). Тосты yellow/green —
 * по переходам online→offline и обратно (идемпотентно через mantine notification id).
 */
export function useChatAIStatus({ enabled }: UseChatAIStatusOptions): UseChatAIStatusResult {
    const [health, setHealth] = useState<IChatAIHealth | null>(null);
    const [lastUpdate, setLastUpdate] = useState<number | null>(null);
    const connectionState = useSyncExternalStore(
        subscribeToHubState,
        getHubStateSnapshot,
        getHubStateSnapshot,
    );

    const lastFetchTs = useRef<number>(0);
    const fetchEpoch = useRef<number>(0);
    const failureStreak = useRef<number>(0);

    const triggerFetch = useCallback(() => {
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
            return;
        }
        const epoch = ++fetchEpoch.current;
        ChatAIService.getHealth()
            .then((data) => {
                if (epoch !== fetchEpoch.current) return;
                failureStreak.current = 0;
                setHealth(data);
                setLastUpdate(Date.now());
                lastFetchTs.current = Date.now();
            })
            .catch(() => {
                if (epoch !== fetchEpoch.current) return;
                failureStreak.current += 1;
                // Один transient error (например, 502 на одну итерацию) не должен мигать
                // бейджем offline — держим last good health до второго подряд провала.
                if (failureStreak.current >= 2) {
                    setHealth(null);
                }
                setLastUpdate(Date.now());
                lastFetchTs.current = Date.now();
            });
    }, []);

    useEffect(() => {
        if (!enabled) {
            // Бамп epoch — инвалидирует все inflight-промисы (их .then/.catch уже не запишут в state).
            fetchEpoch.current += 1;
            failureStreak.current = 0;
            return;
        }
        triggerFetch();
        const intervalId = window.setInterval(triggerFetch, POLL_INTERVAL_MS);
        return () => {
            window.clearInterval(intervalId);
            fetchEpoch.current += 1;
            failureStreak.current = 0;
        };
    }, [enabled, triggerFetch]);

    // SignalR ref-counted start/stop. Подписка на состояние — через useSyncExternalStore выше.
    useEffect(() => {
        if (!enabled) return;
        void chatAIHub.start();
        return () => {
            chatAIHub.stop();
        };
    }, [enabled]);

    // Возврат во вкладку → refresh с cooldown 10s.
    useEffect(() => {
        if (!enabled) return;
        const onVis = () => {
            if (document.visibilityState !== 'visible') return;
            if (Date.now() - lastFetchTs.current < VISIBILITY_REFRESH_COOLDOWN_MS) return;
            triggerFetch();
        };
        document.addEventListener('visibilitychange', onVis);
        return () => document.removeEventListener('visibilitychange', onVis);
    }, [enabled, triggerFetch]);

    const status: ChatAIIndicatorStatus = useMemo(() => {
        if (!enabled) return 'unknown';
        // Bootstrap-фаза: пока хаб ещё не подключился (connecting) или health
        // ни разу не пришёл — никакой бейдж не рендерим. Иначе при первом open
        // успели бы показать "недоступен" между mount и завершением start().
        if (connectionState === 'connecting') return 'unknown';
        if (lastUpdate == null) return 'unknown';
        if (connectionState === 'reconnecting') return 'reconnecting';
        if (health == null) return 'offline';
        if (health.status === 'down' || health.status === 'not_configured') return 'offline';
        if (connectionState === 'disconnected') return 'offline';
        if (health.status === 'degraded') return 'degraded';
        return 'online';
    }, [enabled, health, connectionState, lastUpdate]);

    // Toast при первой смене из стабильного online/degraded в offline/reconnecting и обратно.
    const prevStatusRef = useRef<ChatAIIndicatorStatus>('unknown');
    useEffect(() => {
        // При выключении (закрыли чат) сбрасываем «память» предыдущего статуса. Иначе при
        // следующем открытии stale prev='offline' + новый status='online' выстрелит ложный
        // зелёный toast «AI снова в сети», хотя предыдущая сессия уже закрылась.
        if (!enabled) {
            prevStatusRef.current = 'unknown';
            return;
        }
        const prev = prevStatusRef.current;
        prevStatusRef.current = status;
        if (prev === 'unknown') return; // первый mount — без тостов
        const wasUp = prev === 'online' || prev === 'degraded';
        const isDown = status === 'offline' || status === 'reconnecting';
        if (wasUp && isDown) {
            sendNotification({ ...TOAST_OFFLINE, variant: 'yellow' });
            return;
        }
        const wasDown = prev === 'offline' || prev === 'reconnecting';
        const isUp = status === 'online' || status === 'degraded';
        if (wasDown && isUp) {
            sendNotification({ ...TOAST_ONLINE, variant: 'green' });
        }
    }, [status, enabled]);

    return {
        status,
        health,
        lastUpdate,
        notConfigured: health?.status === 'not_configured',
    };
}
